import { IFileResponse } from '@api/client';
import { AddingFile } from '@ui-components/file-adding/AddingFile.class';
import { EmitMessage, MessageUserInfo } from '@services/web-socket.interfaces';
import { MessageData } from './chat-message-data.interfaces';

// export class FileInfo {
//   file: string;
//   name: string;
//   size: number;
//   fileObj: Blob;
// }

export enum CHAT_TYPE {
  ALL = 'all',
  REPLY = 'reply',
  OFFER = 'offer',
  FREELANCE = 'freelance',
  USER = 'user',
}

export const enum MESSAGE_TYPE {
  TASK = 1,
  USER = 2,
  REPLY = 3,
  OFFER = 4,
  MESSAGE_SYSTEM = 5,
}

export const TASK_RELATED_CHAT_TYPE_IDS: ReadonlySet<number> = new Set<number>([
  MESSAGE_TYPE.TASK,
  MESSAGE_TYPE.REPLY,
  MESSAGE_TYPE.OFFER,
]);

export function normalizeChatTypeId(value: string | number | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (!normalizedValue) {
    return 0;
  }

  const numericValue = Number(normalizedValue);
  if (Number.isFinite(numericValue)) {
    return Math.trunc(numericValue);
  }

  switch (normalizedValue) {
    case 'task':
      return MESSAGE_TYPE.TASK;
    case 'reply':
      return MESSAGE_TYPE.REPLY;
    case 'offer':
      return MESSAGE_TYPE.OFFER;
    default:
      return 0;
  }
}

export function isTaskRelatedChatType(value: string | number | null | undefined): boolean {
  return TASK_RELATED_CHAT_TYPE_IDS.has(normalizeChatTypeId(value));
}

export enum FREELANCE_CHAT_STATUS {
  UNPUBLISHED = '1',
  PUBLISHED = '2',
  DONE = '3',
  WORK = '4',
}

export class MessageVm {
  _id?: string;
  chat_id: number;
  created_at: string;
  edited_at?: string | null;
  updated_at?: string | null;
  from_id: number;
  message: string;
  room?: string;
  to_id?: number | null;
  type: number;
  //user_info: UserResponse;
  files: any[];
  reply_message: MessageVm = null;
  guid: string;
  is_delivered?: boolean = null;
  readBy: number[];
  readByAt?: Record<number, string | null>;
  is_read?: boolean = null;
  isServiceMessage: boolean;
  isCallMessage?: boolean;
  callDirection?: CallMessageDirection | null;
  callStatus?: number | null;
  emittedMessage: EmitMessage = null;
  orderNumber: number;
  filesLength?: number;
  linkPreviews?: MessageLinkPreview[];
  data: MessageData | null;
  user_info?: MessageUserInfo;
  pendingUpload?: boolean;
  pendingFailed?: boolean;
  pendingFiles?: FilesToSendVm[];

  public static fromEmitMessage(emittedMessage: EmitMessage, from_id: number, replyMessage: MessageVm, orderNumber: number): MessageVm {
    return {
      chat_id: emittedMessage.chat_id,
      message: emittedMessage.message || '',
      orderNumber: orderNumber,
      files: [],
      reply_message: replyMessage,
      to_id: emittedMessage.to_id,
      guid: emittedMessage.guid,
      from_id: from_id,
      is_delivered: null,
      readBy: [],
      created_at: new Date().toUTCString(),
      edited_at: null,
      updated_at: null,
      emittedMessage: emittedMessage,
      linkPreviews: [],
      data: emittedMessage.data ?? null,
    } as MessageVm;
  }
}

export interface MessageLinkPreview {
  url: string;
  type: 'image' | 'video';
  mimeType?: string;
  hasError?: boolean;
}

export class MessagesSortByDateVm {
  date: string;
  messages: MessageVm[];
  _id: string;
}

export enum CHAT_CALL_STATUS {
  // TODO: Rename legacy call_fail to outgoing_call_fail and run a message-code migration.
  FAIL = 'call_fail',
  INCOMING_FAIL = 'incoming_call_fail',
  INCOMING = 'incoming_call',
  OUTGOING = 'outgoing_call',
}

export type CallMessageDirection = CHAT_CALL_STATUS.INCOMING | CHAT_CALL_STATUS.OUTGOING;

export class FilesToSendVm {
  file: IFileResponse;
  isSent: boolean;
  uploadId?: string;
  failed?: boolean;
  s3Key?: string;
  addingFile?: AddingFile;
  pendingGuid?: string;
  deferredUpload?: boolean;
  uploadProgress?: number;
  uploadedBytes?: number;
  totalBytes?: number;
  awaitingServer?: boolean;
  awaitingStartedAt?: number;
  nextStatusCheckAt?: number;

  constructor(
    file: IFileResponse,
    isSent: boolean,
    options?: {
      uploadId?: string;
      failed?: boolean;
      s3Key?: string;
      addingFile?: AddingFile;
      pendingGuid?: string;
      deferredUpload?: boolean;
      uploadProgress?: number;
      uploadedBytes?: number;
      totalBytes?: number;
      awaitingServer?: boolean;
      nextStatusCheckAt?: number;
    }
  ) {
    this.file = file;
    this.isSent = isSent;
    this.uploadId = options?.uploadId;
    this.failed = options?.failed;
    this.s3Key = options?.s3Key;
    this.addingFile = options?.addingFile;
    this.pendingGuid = options?.pendingGuid;
    this.deferredUpload = options?.deferredUpload;
    this.uploadProgress = options?.uploadProgress;
    this.uploadedBytes = options?.uploadedBytes;
    this.totalBytes = options?.totalBytes;
    this.awaitingServer = options?.awaitingServer;
    this.nextStatusCheckAt = options?.nextStatusCheckAt;
  }
}
